import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Divider,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { LoadingButton } from "@material-ui/lab";
import DownloadIcon from "@material-ui/icons/Download";
import { useSnackbar } from "notistack";
import Label from "../../Label";
import { formatearFecha } from "../../../utils/formatTime";
import {
  useObtenerDetalleFacturaClientePlanesQuery,
  useObtenerPagosTareasFacturaQuery,
} from "../../../services/accesoClientesApi";
import { descargarPdfDteProxy } from "../../../utils/sharedFunctions";
import { useDescargarPdfDteMutation } from "../../../services/dteApi";

// Helper para formatear números a formato chileno
function formatNumber(n) {
  n = n > 0 ? String(n).replace(/\D/g, "") : n;
  return n === "" ? n : Number(n).toLocaleString("es-CL");
}

// Función para formatear valores en pesos chilenos
const formatAsCLP = (value) => {
  if (!value) return "$0";
  return `$${formatNumber(value)}`;
};

const ModalVerFacturaCliente = ({ open, onClose, factura, tipo }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loadingDescargar, setLoadingDescargar] = useState(false);
  const [descargarPdf] = useDescargarPdfDteMutation();

  // Llamar AMBOS hooks siempre, pero skipear el que no necesitamos
  const {
    data: pagosPlanes = [],
    isLoading: isLoadingPlanes,
    isError: isErrorPlanes,
    error: errorPlanes,
  } = useObtenerDetalleFacturaClientePlanesQuery(factura?.folio, {
    skip: !open || !factura?.folio || tipo !== "planes",
  });

  const {
    data: pagosTareas = [],
    isLoading: isLoadingTareas,
    isError: isErrorTareas,
    error: errorTareas,
  } = useObtenerPagosTareasFacturaQuery(factura?.id, {
    skip: !open || !factura?.id || tipo !== "tareas",
  });

  // Seleccionar los datos correctos según el tipo
  const pagos = tipo === "planes" ? pagosPlanes : pagosTareas;
  const isLoadingPagos = tipo === "planes" ? isLoadingPlanes : isLoadingTareas;
  const isErrorPagos = tipo === "planes" ? isErrorPlanes : isErrorTareas;
  const errorPagos = tipo === "planes" ? errorPlanes : errorTareas;

  // Descarga el PDF por el proxy backend (la apikey vive solo en el backend).
  const handleDescargarPDF = async () => {
    if (!factura?.id) {
      enqueueSnackbar("No se puede descargar: documento no disponible", {
        variant: "warning",
      });
      return;
    }
    setLoadingDescargar(true);
    try {
      // `tipo` es la prop del componente ("planes" | "tareas"); se mapea al tipo
      // que espera el backend en una variable local para no colisionar con ella.
      const tipoDte = tipo === "planes" ? "plan" : "tarea";
      await descargarPdfDteProxy({
        trigger: descargarPdf,
        tipo: tipoDte,
        id: factura.id,
        nombreArchivo: `Factura_${factura.folio}.pdf`,
        enqueueSnackbar,
      });
    } finally {
      setLoadingDescargar(false);
    }
  };

  // Calcular total de pagos
  const totalPagado = pagos.reduce(
    (sum, pago) => sum + (pago.valorPesos || 0),
    0,
  );

  const valorFactura = factura?.montoOriginal ?? factura?.total;
  // El saldo visible es el calculado por el endpoint consolidado.
  const saldoPendiente = factura?.montoRestante ?? 0;

  // Si no hay factura, no renderizar
  if (!factura) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="factura-dialog-title"
    >
      <DialogTitle id="factura-dialog-title">
        Factura N° {factura.folio || "Sin folio"}
      </DialogTitle>

      <DialogContent>
        {/* Información de la Factura */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            📄 Información de la Factura
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Sociedad
              </Typography>
              <Typography variant="body1" gutterBottom>
                {factura.nombreSociedad || "Sociedad sin nombre"} ·{" "}
                {factura.rutSociedad}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">
                Folio
              </Typography>
              <Typography variant="body1" gutterBottom>
                {factura.folio || "-"}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">
                Periodo
              </Typography>
              <Typography variant="body1" gutterBottom>
                {factura.fecha
                  ? formatearFecha(factura.fecha, "MMMM [del] YYYY", "YYYY/MM")
                  : "-"}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">
                Fecha Facturación
              </Typography>
              <Typography variant="body1" gutterBottom>
                {factura.fechaFacturacion
                  ? formatearFecha(
                      factura.fechaFacturacion,
                      "DD/MM/YYYY",
                      "YYYY/MM/DD",
                    )
                  : "-"}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">
                Valor Total
              </Typography>
              <Typography variant="h6" color="primary">
                {formatAsCLP(valorFactura)}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">
                Estado Factura
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Label
                  variant="ghost"
                  color={
                    factura.estadoFactura === "PAGADO"
                      ? "success"
                      : factura.estadoFactura === "FACTURADO"
                        ? "warning"
                        : factura.estadoFactura === "ABONO"
                          ? "info"
                          : "error"
                  }
                >
                  {factura.estadoFactura}
                </Label>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">
                Estado Correo
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Label
                  variant="ghost"
                  color={
                    factura.estadoCorreo === "ENVIADO" ? "success" : "error"
                  }
                >
                  {factura.estadoCorreo || "NO ENVIADO"}
                </Label>
              </Box>
            </Grid>

            {/* Mostrar nombreTarea si es factura de tareas */}
            {tipo === "tareas" && factura.nombreTarea && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  Tarea
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {factura.nombreTarea}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
        {/* Sección de Pagos */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            💰 Pagos Realizados
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* Estado de carga de pagos */}
          {isLoadingPagos && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error al cargar pagos */}
          {isErrorPagos && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error al cargar los pagos. Por favor, intenta nuevamente.
              {errorPagos?.data?.message && ` (${errorPagos.data.message})`}
            </Alert>
          )}

          {/* Tabla de pagos */}
          {!isLoadingPagos && !isErrorPagos && (
            <>
              {pagos.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fecha Registro</TableCell>
                        <TableCell>Fecha Pago</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell>Método</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagos.map((pago, index) => (
                        <TableRow key={`pago-${index}`} hover>
                          <TableCell>
                            {pago.fechaCreacion ? pago.fechaCreacion : "-"}
                          </TableCell>
                          <TableCell>
                            {pago.fechaPago ? pago.fechaPago : "-"}
                          </TableCell>
                          <TableCell align="right">
                            {formatAsCLP(pago.valorPesos)}
                          </TableCell>
                          <TableCell>{pago.metodoPago || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  No se han registrado pagos para esta factura.
                </Alert>
              )}
            </>
          )}
        </Box>
        {/* Resumen de pagos */}
        {!isLoadingPagos && !isErrorPagos && pagos.length > 0 && (
          <Box
            sx={{ mt: 2, p: 2, bgcolor: "background.default", borderRadius: 1 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Total Pagado:
                </Typography>
                <Typography variant="h6" color="success.main">
                  {formatAsCLP(totalPagado)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Saldo Pendiente:
                </Typography>
                <Typography
                  variant="h6"
                  color={saldoPendiente > 0 ? "warning.main" : "success.main"}
                >
                  {formatAsCLP(saldoPendiente)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      {/* Acciones del Dialog */}
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cerrar
        </Button>
        <LoadingButton
          startIcon={<DownloadIcon />}
          loading={loadingDescargar}
          onClick={handleDescargarPDF}
          variant="contained"
          color="primary"
          disabled={!factura.documentoDisponible}
        >
          Descargar PDF
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

ModalVerFacturaCliente.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  factura: PropTypes.object,
  tipo: PropTypes.oneOf(["planes", "tareas"]).isRequired,
};

export default ModalVerFacturaCliente;
